import React, { useState } from 'react';
import { Dialog, DialogContent } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import API_BASE_URL from '../../../../../Constants';

// Import your data arrays
import {
  devicePartOptions,
  violatingConditionOptions,
  suggestedRemedyOptions,
  resolutionMethodOptions,
  itemStatusOptions,
} from './ELV3DataConfig';

// The table that fetches violations from the server
import ViolationsTable from './ViolationsTable';

const CATEGORY_LIST = [
  'Inside Car',
  'Outside Hoistway',
  'Top of Car',
  'Machine Room',
  'Pit',
  'Escalator/Moving Walk',
  'Entire Device',
  'All Types',
];

const ViolationsEntryModal = ({
  open,
  onClose,
  inspectionNumber,
  buildingName,
  deviceId,
  deviceName,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // "Create New Violation" form states
  const [newViolationDate, setNewViolationDate] = useState('');
  const [newDevicePart, setNewDevicePart] = useState('');
  const [newViolatingCondition, setNewViolatingCondition] = useState('');
  const [newSuggestedRemedy, setNewSuggestedRemedy] = useState('');
  const [newAdditionalComment, setNewAdditionalComment] = useState('');
  const [newEstimatedAmount, setNewEstimatedAmount] = useState('');
  const [newResolutionMethod, setNewResolutionMethod] = useState('');
  const [newItemStatus, setNewItemStatus] = useState('OPEN');

  // SINGLE‐SELECTION Category
  // If user clicks a new category, that one is chosen; if they click the same one again => none
  const [selectedCategory, setSelectedCategory] = useState('All Types');

  // Force the <ViolationsTable> to re-fetch after adding
  const [needsRefresh, setNeedsRefresh] = useState(false);

  const toggleExpand = () => setIsExpanded(!isExpanded);
  const handleClose = () => onClose();

  // Single‐selection logic
  const handleCategoryChange = (category) => {
    if (selectedCategory === category) {
      // Already selected => uncheck
      setSelectedCategory('');
    } else {
      // Select new category
      setSelectedCategory(category);
    }
  };

  // Filter the devicePart array for the chosen category
  const filteredDeviceParts = devicePartOptions.filter((partObj) => {
    if (!selectedCategory || selectedCategory === '') {
      // No category selected => empty
      return false;
    }
    if (selectedCategory === 'All Types') return true;
    return partObj.categories.includes(selectedCategory);
  });

  // “Add Violation” => POST
  const handleAddViolation = async () => {
    if (!inspectionNumber || !deviceId) {
      alert('Cannot create a violation without an inspectionNumber and deviceId.');
      return;
    }

    const payload = {
      inspectionId: parseInt(inspectionNumber, 10),
      deviceId: parseInt(deviceId, 10),
      violationDate: newViolationDate || null,
      devicePart: newDevicePart || '',
      violatingCondition: newViolatingCondition || '',
      suggestedRemedy: newSuggestedRemedy || '',
      additionalComment: newAdditionalComment || '',
      resolutionMethod: newResolutionMethod || '',
      itemStatus: newItemStatus || 'OPEN',
    };
    if (newEstimatedAmount) {
      payload.estimatedAmount = parseFloat(newEstimatedAmount);
    }

    try {
      const resp = await fetch(`${API_BASE_URL}/api/violations`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        throw new Error(`Failed to create violation. Status: ${resp.status}`);
      }

      // Clear the form
      setNewViolationDate('');
      setNewDevicePart('');
      setNewViolatingCondition('');
      setNewSuggestedRemedy('');
      setNewAdditionalComment('');
      setNewEstimatedAmount('');
      setNewResolutionMethod('');
      setNewItemStatus('OPEN');

      alert('New violation created successfully!');
      // Re-fetch
      setNeedsRefresh((prev) => !prev);
    } catch (err) {
      console.error('Error creating violation:', err);
      alert('Failed to create violation.');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        style: {
          width: isExpanded ? '90%' : '900px',
          maxHeight: '72vh',
          minHeight: '72vh',
        },
      }}
      style={{ zIndex: 1400 }}
    >
      {/* Header */}
      <div className="sticky top-0 bg-white z-10">
        <div className="flex justify-between items-center p-4 border-b border-gray-300">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleClose}
              className="p-2 rounded-full hover:bg-gray-200 transition"
            >
              <ArrowBackIcon />
            </button>
            <h2 className="text-2xl font-bold">
              Violations for Device: {deviceName || `ID#${deviceId}`}
            </h2>
          </div>
          <button
            onClick={toggleExpand}
            className="p-2 rounded-full hover:bg-gray-200 transition"
          >
            {isExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </button>
        </div>
      </div>

      <DialogContent dividers className="pb-2">
        {/* Info */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 space-y-2 md:space-y-0">
          <div className="text-sm space-y-1">
            <div>
              <strong>Inspection #:</strong> {inspectionNumber}
            </div>
            <div>
              <strong>Building:</strong> {buildingName}
            </div>
            <div>
              <strong>Device ID:</strong> {deviceId}
            </div>
          </div>
        </div>

        {/* Category checkboxes */}
        <div className="mb-4 border border-gray-300 p-3 rounded">
          <h3 className="font-semibold mb-2">Filter by Device Part Category:</h3>
          <div className="flex flex-wrap gap-4">
            {CATEGORY_LIST.map((cat) => {
              const isChecked = (selectedCategory === cat);
              return (
                <label
                  key={cat}
                  className="flex items-center text-sm cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => handleCategoryChange(cat)}
                    className="mr-1"
                  />
                  {cat}
                </label>
              );
            })}
          </div>
        </div>

        {/* CREATE NEW VIOLATION FORM */}
        <div className="mb-4 border border-gray-300 rounded p-3">
          <h3 className="font-bold mb-2">Create New Violation</h3>

          {/* Violation Date */}
          <div className="flex flex-col gap-2 md:flex-row mb-2">
            <label className="w-40 font-medium text-sm">Violation Date:</label>
            <input
              type="date"
              value={newViolationDate}
              onChange={(e) => setNewViolationDate(e.target.value)}
              className="border border-gray-300 p-1 rounded md:w-60"
            />
          </div>

          {/* Device Part (filtered) */}
          <div className="flex flex-col gap-2 md:flex-row mb-2">
            <label className="w-40 font-medium text-sm">Device Part:</label>
            <select
              value={newDevicePart}
              onChange={(e) => setNewDevicePart(e.target.value)}
              className="border border-gray-300 p-1 rounded md:w-60"
            >
              <option value="">-- Select Part --</option>
              {filteredDeviceParts.map((partObj) => (
                <option key={partObj.label} value={partObj.label}>
                  {partObj.label}
                </option>
              ))}
            </select>
          </div>

          {/* Violating Condition */}
          <div className="flex flex-col gap-2 md:flex-row mb-2">
            <label className="w-40 font-medium text-sm">Violating Condition:</label>
            <select
              value={newViolatingCondition}
              onChange={(e) => setNewViolatingCondition(e.target.value)}
              className="border border-gray-300 p-1 rounded md:w-60"
            >
              <option value="">-- Select Condition --</option>
              {violatingConditionOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>

          {/* Suggested Remedy */}
          <div className="flex flex-col gap-2 md:flex-row mb-2">
            <label className="w-40 font-medium text-sm">Suggested Remedy:</label>
            <select
              value={newSuggestedRemedy}
              onChange={(e) => setNewSuggestedRemedy(e.target.value)}
              className="border border-gray-300 p-1 rounded md:w-60"
            >
              <option value="">-- Select Remedy --</option>
              {suggestedRemedyOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>

          {/* Additional Comment */}
          <div className="flex flex-col gap-2 md:flex-row mb-2">
            <label className="w-40 font-medium text-sm">Additional Comment:</label>
            <input
              type="text"
              value={newAdditionalComment}
              onChange={(e) => setNewAdditionalComment(e.target.value)}
              className="border border-gray-300 p-1 rounded md:w-60"
            />
          </div>

          {/* Estimated Amount */}
          <div className="flex flex-col gap-2 md:flex-row mb-2">
            <label className="w-40 font-medium text-sm">Estimated Amount:</label>
            <input
              type="number"
              step="0.01"
              value={newEstimatedAmount}
              onChange={(e) => setNewEstimatedAmount(e.target.value)}
              className="border border-gray-300 p-1 rounded md:w-60"
            />
          </div>

          {/* Resolution Method */}
          <div className="flex flex-col gap-2 md:flex-row mb-2">
            <label className="w-40 font-medium text-sm">Resolution Method:</label>
            <select
              value={newResolutionMethod}
              onChange={(e) => setNewResolutionMethod(e.target.value)}
              className="border border-gray-300 p-1 rounded md:w-60"
            >
              <option value="">-- Select Method --</option>
              {resolutionMethodOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>

          {/* Item Status */}
          <div className="flex flex-col gap-2 md:flex-row mb-2">
            <label className="w-40 font-medium text-sm">Item Status:</label>
            <select
              value={newItemStatus}
              onChange={(e) => setNewItemStatus(e.target.value)}
              className="border border-gray-300 p-1 rounded md:w-60"
            >
              <option value="">-- Select Status --</option>
              {itemStatusOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end mt-3">
            <button
              onClick={handleAddViolation}
              className="px-4 py-1 bg-blue-600 text-white rounded"
            >
              Add Violation
            </button>
          </div>
        </div>

        {/* Existing Violations Table => triggers refetch on needsRefresh */}
        <ViolationsTable
          inspectionNumber={inspectionNumber}
          deviceId={deviceId}
          needsRefresh={needsRefresh}
        />
      </DialogContent>
    </Dialog>
  );
};

export default ViolationsEntryModal;
